import React from 'react'
import {
  List,
  useTable,
  ShowButton,
  DateField,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { UsersRound, Bell, Newspaper, CalendarCheck } from '../../components/icons'

type UserSetting = Tables<'user_settings'>
type Profile = Tables<'profiles'>

export function UserSettingList() {
  const { tableProps } = useTable<UserSetting>({
    syncWithLocation: true,
  })

  // Summary deck — bounded count queries (pageSize:1 → PostgREST returns the
  // total without the rows). Replaces the old unbounded useList over the whole
  // `user_settings` table. Total members + the three boolean=true filtered
  // counts run in parallel as tiny requests.
  const { result: totalRes } = useList<UserSetting>({
    resource: 'user_settings',
    pagination: { pageSize: 1 },
  })
  const { result: pushRes } = useList<UserSetting>({
    resource: 'user_settings',
    filters: [{ field: 'push', operator: 'eq', value: true }],
    pagination: { pageSize: 1 },
  })
  const { result: digestRes } = useList<UserSetting>({
    resource: 'user_settings',
    filters: [{ field: 'digest', operator: 'eq', value: true }],
    pagination: { pageSize: 1 },
  })
  const { result: remindersRes } = useList<UserSetting>({
    resource: 'user_settings',
    filters: [{ field: 'reminders', operator: 'eq', value: true }],
    pagination: { pageSize: 1 },
  })
  const totalMembers = totalRes?.total ?? 0
  const pushOn = pushRes?.total ?? 0
  const digestOn = digestRes?.total ?? 0
  const remindersOn = remindersRes?.total ?? 0

  // Resolve user_id -> profiles.name, scoped to ONLY the members on the current
  // page (IN filter), not the whole profiles table.
  const rows = (tableProps.dataSource ?? []) as UserSetting[]
  const userIds = React.useMemo(
    () => Array.from(new Set(rows.map((s) => s.user_id).filter(Boolean))) as string[],
    [rows],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    filters: userIds.length ? [{ field: 'user_id', operator: 'in', value: userIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: userIds.length > 0 },
  })

  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(
      items
        .filter((p) => p.user_id != null)
        .map((p) => [p.user_id as string, p.name]),
    )
  }, [profileRes])

  const stats: Stat[] = [
    { label: 'Total Members', value: totalMembers.toLocaleString(), icon: UsersRound, tone: 'jaguar' },
    { label: 'Push On', value: pushOn.toLocaleString(), icon: Bell, tone: 'gold' },
    { label: 'Digest On', value: digestOn.toLocaleString(), icon: Newspaper, tone: 'green' },
    { label: 'Reminders On', value: remindersOn.toLocaleString(), icon: CalendarCheck, tone: 'gold' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Members · Member Settings',
        description:
          'Per-member notification and privacy preferences synced from the mobile app — push, digest, reminders, photo sharing, directory visibility, and Face ID.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false} canCreate={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="user_id" scroll={{ x: true }}>
            <Table.Column<UserSetting>
              dataIndex="user_id"
              title="Member"
              render={(userId: string) => profileMap[userId] ?? userId}
            />
            <Table.Column<UserSetting>
              dataIndex="language"
              title="Language"
            />
            <Table.Column<UserSetting>
              dataIndex="push"
              title="Push"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="digest"
              title="Digest"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="reminders"
              title="Reminders"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="photos"
              title="Photos"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="directory"
              title="Directory"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="faceid"
              title="Face ID"
              render={(v: boolean) => <Pill tone={v ? 'green' : 'neutral'}>{v ? 'On' : 'Off'}</Pill>}
            />
            <Table.Column<UserSetting>
              dataIndex="updated_at"
              title="Updated"
              render={(value: string) => (
                <DateField value={value} format="MMM D, YYYY" />
              )}
            />
            <Table.Column<UserSetting>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: UserSetting) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.user_id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
